// One dashboard: section/markdown layout (§2), viz_type dispatch (§3), the
// filter bar (§4), progressive rendering + request supersession (§6, via
// lib/bi-query.ts), and the loading/empty/error states for both a single
// chart (ChartCard's own job) and the whole dashboard (§7, this page's job).
//
// The URL carries the dashboard's SLUG (bookmarkable), but
// `GET /api/bi/dashboards/{id}` (`bi/repo/bi_registry.get_dashboard`) looks up
// by id. The catalog (`dashboardsQuery`) is already in cache before any route
// renders (`App.tsx`'s `Boot()`), so resolving slug -> id costs no round trip.
import { type ReactNode, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import type { ChartSpec, ChartTile, DatasetSpec } from "@bi/shared/types";

import { ChartCard } from "../components/chart-card";
import { FilterBar } from "../components/filter-bar";
import { Markdown } from "../components/markdown";
import { EmptyState, ErrorState, LoadingState } from "../components/states";
import { EMPLOYEE_EMAIL } from "../config";
import { ApiError } from "../lib/api";
import { useBiDashboardQuery } from "../lib/bi-query";
import { GRID_COLUMNS, columnSpans, sizeClass, toSections } from "../lib/dashboard-layout";
import type { AuthoredConstraint } from "../lib/filter-state";
import {
  authoredConstraintsOnFilteredColumns,
  chartIgnoresDateRange,
  defaultFilterState,
  isDefaultTimeRange,
  migrateLegacyCohortWindow,
  parseFilterState,
  resetTimeRangeToDefault,
  resolveFilterScope,
  serializeFilterState,
  setTimeRange,
  withDefaultSelections,
} from "../lib/filter-state";
import { defaultRangeLabel, describeTimeRange } from "../lib/time-range";
import { useFilterState } from "../lib/filter-store";
import { dashboardQuery, dashboardsQuery } from "../lib/queries";

const EMPTY_CHARTS: Record<string, ChartSpec> = {};
const EMPTY_DATASETS: Record<string, DatasetSpec> = {};
const EMPTY_CONSTRAINTS: AuthoredConstraint[] = [];

/** One dashboard's authored `css`, SCOPED to that dashboard.
 *
 *  The authored text is wrapped in a `[data-dashboard="<slug>"]` block and
 *  relies on native CSS nesting to prefix every selector inside it, so a
 *  dashboard's styling cannot leak into the app shell or into another
 *  dashboard — which is the whole reason the field is safe to honour at all.
 *  Custom properties declared at the top level of the authored text (the
 *  `--rc-chart-*` slots especially) land on the page root and inherit down
 *  into every chart, which is how a dashboard re-tints its own series.
 *
 *  `</style>` is neutralised before injection: the spec tree is reviewed and
 *  CI-gated, but a style element is the one place where unbalanced text could
 *  end the element early and change what the rest of the document means. */
function DashboardCss({ slug, css }: { slug: string; css: string }): ReactNode {
  const scoped = useMemo(
    () => `[data-dashboard="${CSS.escape(slug)}"] {\n${css.replace(/<\/(style)/gi, "<\\/$1")}\n}`,
    [slug, css],
  );
  return <style>{scoped}</style>;
}

export function DashboardPage({
  slug,
  initialFiltersParam,
}: {
  slug: string;
  initialFiltersParam?: string;
}) {
  const dashboards = useQuery(dashboardsQuery);
  const summary = dashboards.data?.dashboards.find((d) => d.slug === slug);

  const dashboard = useQuery({
    ...dashboardQuery(summary?.id ?? ""),
    enabled: summary !== undefined,
  });

  const navigate = useNavigate({ from: "/d/$slug" });
  const [filters, setFilters] = useFilterState();

  // Stable empty-object fallbacks (module-level, not a fresh `{}` literal
  // per render) so the useMemo hooks below that key on `charts`/`datasets`
  // actually memoize instead of recomputing on every render. Declared up here
  // rather than beside their first use because the seeding effect below reads
  // `spec` in its dependency array, which is evaluated during render.
  const charts = dashboard.data?.charts ?? EMPTY_CHARTS;
  const datasets = dashboard.data?.datasets ?? EMPTY_DATASETS;
  const spec = dashboard.data?.spec;

  // Seed the store from the URL once per navigation to a dashboard. After that
  // the store is the source of truth and writes back to the URL, never the other
  // way. Seeding must RESET, not just apply: the filter store is module-level and
  // shared across dashboards, so no `?filters=` means no filters (otherwise the
  // previous dashboard's selections leak in).
  // Waits for `spec`: a select filter can declare its default selection
  // (`withDefaultSelections`), and for a column that partitions its dataset into
  // disjoint slices the first paint would otherwise union every slice. Nothing
  // fetches before the spec lands, so the wait costs nothing.
  const seededSlugRef = useRef<string | null>(null);
  useEffect(() => {
    if (!spec || seededSlugRef.current === slug) return;
    seededSlugRef.current = slug;
    setFilters(
      initialFiltersParam
        ? // A shared link is a snapshot of what its sender was looking at, so
          // it is applied verbatim: filling in a default the sender had
          // removed would make their link show something else.
          migrateLegacyCohortWindow(parseFilterState(initialFiltersParam), datasets)
        : withDefaultSelections(defaultFilterState(), spec.filters),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed-once-per-slug by design
  }, [slug, spec]);

  const handleFiltersChange = (next: typeof filters) => {
    setFilters(next);
    // `resetScroll: false`: this navigation only syncs `?filters=` with the
    // store. TanStack Router defaults `resetScroll` to true, which threw the
    // reader to the top on every filter write (worst on drag-to-zoom).
    void navigate({
      search: (prev) => ({ ...prev, filters: serializeFilterState(next) }),
      replace: true,
      resetScroll: false,
    });
  };

  // Drag-to-zoom (a chart-card's brush wrapper, lib/time-brush.ts) applies
  // its resolved bucket range through the SAME path every other filter
  // change takes — `handleFiltersChange` already persists to the store AND
  // writes back to the URL, so a zoomed-in view is shareable exactly like a
  // preset pick. `setTimeRange` always writes both bounds together (never
  // one alone), matching the backend's own all-or-nothing contract.
  const handleZoomToRange = (start: string, end: string) => {
    handleFiltersChange(setTimeRange(filters, start, end));
  };
  // Back to the DEFAULT window, not to All time. Clearing the bounds means
  // "all time", which is a deliberate destination a reader can pick from the
  // filter bar — it is not where an undo should land them, and landing there
  // silently widened the very window they were trying to step back from.
  const resetZoom = () => handleFiltersChange(resetTimeRangeToDefault(filters));
  // Shown when the window is not the default one. Keyed on "are any bounds
  // set?" this was true on every load, so the pill stood there permanently
  // announcing a zoom nobody had applied.
  const showRangeBanner = !isDefaultTimeRange(filters);

  const sections = useMemo(() => (spec ? toSections(spec.layout) : []), [spec]);
  const allChartIds = useMemo(
    () =>
      sections.flatMap((section) =>
        section.chartRows.flat().map((tile: ChartTile) => tile.chart_uuid),
      ),
    [sections],
  );

  const filterScopeByChart = useMemo(() => {
    const map = new Map<string, number>();
    if (!spec) return map;
    for (const filter of spec.filters) {
      const scope = resolveFilterScope(filter, charts, datasets);
      for (const s of scope) {
        if (!s.active) map.set(s.chartId, (map.get(s.chartId) ?? 0) + 1);
      }
    }
    return map;
  }, [spec, charts, datasets]);

  // The SERVER's view of who is signed in, not the build-time constant. In cloud
  // the SPA cannot read the HttpOnly `bi_authority` cookie, so `EMPLOYEE_EMAIL`
  // is a build-time default, while `POST /api/bi/pusher/auth` authorizes
  // `channel_for_bi(<real email>)`. Subscribing on the build-time value 403s and
  // leaves tiles spinning. This query already gates the page, so the real email
  // is in hand before the fan-out runs.
  const employeeEmail = dashboards.data?.employeeEmail || EMPLOYEE_EMAIL;
  // A chart's OWN authored filter on a column the reader is filtering. The
  // runtime selection narrows it rather than replacing it, so this is what
  // turns "No data for this filter." into a sentence that explains itself.
  const constraintsByChart = useMemo(() => {
    const map = new Map<string, ReturnType<typeof authoredConstraintsOnFilteredColumns>>();
    for (const [chartId, chart] of Object.entries(charts)) {
      const constraints = authoredConstraintsOnFilteredColumns(chart, filters);
      if (constraints.length > 0) map.set(chartId, constraints);
    }
    return map;
  }, [charts, filters]);

  // Only while a range is actually picked: a chart with no date column is
  // unremarkable until the reader narrows the dashboard and this tile does
  // not narrow with it. The server drops the range for exactly these charts
  // (`orchestration.scope_filters_for_chart`) rather than erroring them, so
  // without the note the tile silently answers for all time.
  const timelessCharts = useMemo(() => {
    const ids = new Set<string>();
    if (!filters.time_range_start && !filters.time_range_end) return ids;
    for (const [chartId, chart] of Object.entries(charts)) {
      if (chartIgnoresDateRange(chart, datasets[chart.dataset_uuid])) ids.add(chartId);
    }
    return ids;
  }, [charts, datasets, filters.time_range_start, filters.time_range_end]);

  const { charts: chartStates, retryChart } = useBiDashboardQuery(
    summary?.id ?? "",
    allChartIds,
    filters,
    employeeEmail,
  );

  if (dashboards.isPending) return <LoadingState label="Loading dashboards…" />;
  if (dashboards.isError) {
    return <ErrorState error={dashboards.error} onRetry={() => void dashboards.refetch()} />;
  }
  if (summary === undefined) {
    return (
      <EmptyState
        title="Dashboard not found"
        message="This link may be stale, or the dashboard hasn't been synced."
      />
    );
  }
  if (dashboard.isPending) return <LoadingState label="Loading dashboard…" />;
  if (dashboard.isError) {
    // 403: the audience/companion-area check refused THIS dashboard — actionable
    // ("ask for access"). 404: the id doesn't exist — usually a stale/bad link,
    // not actionable the same way. Worded distinctly rather than one generic
    // "couldn't load" message for both (design doc §7).
    const status = dashboard.error instanceof ApiError ? dashboard.error.status : undefined;
    const title =
      status === 403
        ? "You don't have access to this dashboard"
        : status === 404
          ? "This dashboard doesn't exist"
          : "Couldn't load this dashboard";
    return (
      <ErrorState error={dashboard.error} title={title} onRetry={() => void dashboard.refetch()} />
    );
  }

  if (allChartIds.length === 0) {
    return (
      <div className="bi-page">
        <div className="bi-page__head">
          <h1 className="bi-page__title">{spec?.title}</h1>
        </div>
        <EmptyState
          title="Nothing to chart here"
          message="This dashboard is narrative only, with no charts."
        />
        {sections.map((section, i) =>
          section.markdown ? <Markdown key={i} source={section.markdown} /> : null,
        )}
      </div>
    );
  }

  return (
    <div className={`bi-page${spec?.css ? " bi-page--themed" : ""}`} data-dashboard={spec?.slug}>
      {spec?.css ? <DashboardCss slug={spec.slug} css={spec.css} /> : null}
      <div className="bi-page__head">
        <h1 className="bi-page__title">{spec?.title}</h1>
        {showRangeBanner ? (
          // A drag-to-zoom is started FROM a chart, not the filter bar, so a
          // reader who dragged never necessarily looked at the bar at all —
          // the way back out lives right beside the title instead of asking
          // them to go find it. The label names the DESTINATION rather than
          // saying "Reset zoom": the window may have come from a preset or a
          // shared link rather than a drag, and either way what the reader
          // needs to know is where the button lands them.
          <button type="button" className="bi-zoom-banner" onClick={resetZoom}>
            Showing {describeTimeRange(filters.time_range_start, filters.time_range_end)} · Back to{" "}
            {defaultRangeLabel()}
          </button>
        ) : null}
      </div>
      {spec ? (
        <FilterBar
          filters={spec.filters}
          chartStates={chartStates}
          charts={charts}
          datasets={datasets}
          state={filters}
          onChange={handleFiltersChange}
        />
      ) : null}
      {sections.map((section, i) => (
        <section key={i} className="bi-section">
          {section.markdown ? <Markdown source={section.markdown} /> : null}
          {section.chartRows.map((row, j) => {
            // The authored proportions for THIS row, normalized to fill the
            // grid (lib/dashboard-layout.ts). Computed per row rather than per
            // tile because normalizing is a property of the row as a whole.
            const spans = columnSpans(row);
            return (
              <div key={j} className="bi-card-grid">
                {row.map((tile, k) => {
                  const chart = charts[tile.chart_uuid];
                  if (!chart) return null;
                  return (
                    <ChartCard
                      key={tile.chart_uuid}
                      chart={chart}
                      state={chartStates[tile.chart_uuid] ?? { status: "loading" }}
                      columnSpan={spans[k] ?? GRID_COLUMNS}
                      sizeClass={sizeClass(tile)}
                      inactiveFilterCount={filterScopeByChart.get(tile.chart_uuid) ?? 0}
                      authoredConstraints={
                        constraintsByChart.get(tile.chart_uuid) ?? EMPTY_CONSTRAINTS
                      }
                      ignoresDateRange={timelessCharts.has(tile.chart_uuid)}
                      dataset={datasets[chart.dataset_uuid]}
                      onZoomToRange={handleZoomToRange}
                      onRetry={() => retryChart(tile.chart_uuid)}
                    />
                  );
                })}
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}
